/**
 * Customers in the top-bar search (accounts using Customers): the customers
 * at the current store whose name, phone, email, VIN, stock or job number
 * matches. Quietly nothing when there's no store, the account doesn't use
 * Customers, or the person can't see customers.
 */
import { formatCustomerPhone, listCustomers, type Customer } from '@turn-connect/shared';
import { webApiClient } from '../../services/apiClient';
import { useCustomersOn } from './customersQueries';
import { canReadCustomers } from './customersAccess';

/** Whether the top search should look for customers: the account uses them and the person may see them. */
export function useCustomerSearchOn(accountId: string | null | undefined, permissions: readonly string[]): boolean {
  const { on } = useCustomersOn(accountId, { enabled: canReadCustomers(permissions as string[]) });
  return on && canReadCustomers(permissions as string[]);
}

export async function searchCustomersAt(locationId: string | null | undefined, term: string, cap: number): Promise<Customer[]> {
  if (!locationId || !term.trim()) return [];
  try {
    const page = await listCustomers(webApiClient, locationId, { search: term.trim(), limit: cap });
    return page.data.slice(0, cap);
  } catch {
    return [];
  }
}

/** "Business · (555) 640-1180 · 4 open" under a customer search result. */
export function customerSearchDetail(c: Pick<Customer, 'kind' | 'phone' | 'open_jobs' | 'not_invoiced_jobs'>, kindLabel: string): string {
  return [
    kindLabel,
    formatCustomerPhone(c.phone),
    c.open_jobs ? `${c.open_jobs} open` : '',
    c.not_invoiced_jobs ? `${c.not_invoiced_jobs} not invoiced` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}
